import { runAz } from "./az.js";
import { GATEWAY_SUBSCOPE_GRANT_DESCRIPTION } from "./bootstrap-mi.js";

const ROLE_ASSIGNMENT_API = "2022-04-01";

// The gateway MI's two subscription-scoped reader roles (role-assignments-subscription.bicep).
const GATEWAY_SUBSCOPE_ROLES = ["Cost Management Reader", "Monitoring Reader"];

interface RoleAssignmentRow {
  id: string;
  principalId?: string;
  principalName?: string;
  principalType?: string;
  roleDefinitionName?: string;
  scope?: string;
  description?: string;
  createdOn?: string;
}

async function listSubscriptionAssignments(subscriptionId: string): Promise<RoleAssignmentRow[]> {
  return JSON.parse(
    (await runAz([
      "role", "assignment", "list", "--all", "--subscription", subscriptionId, "-o", "json",
    ]).catch(() => "[]")) || "[]",
  ) as RoleAssignmentRow[];
}

/**
 * True only when the directory answers that this exact object id does not exist:
 * `az ad sp show` fails with "Resource '<id>' does not exist ..." (measured with
 * azure-cli 2.89.0 against a random object id, exit 3). A resolved principal, a
 * refused lookup (no Graph read), or any other failure is not proof.
 */
async function principalIsGone(principalId: string | undefined): Promise<boolean> {
  if (!principalId) return false;
  try {
    await runAz(["ad", "sp", "show", "--id", principalId, "--query", "id", "-o", "tsv"]);
    return false;
  } catch (e) {
    return (e as Error).message.includes(`Resource '${principalId}' does not exist`);
  }
}

// A just-created installer identity can be missing from the directory for a while
// (listAssignmentIds in bootstrap-mi.ts filters by principalId for that reason), so
// a young grant could look orphaned while its install is launching.
const ORPHAN_MIN_AGE_MS = 60 * 60 * 1000;

/**
 * The candidates that are provably orphaned: the listing named no principal (it
 * leaves every name blank when its own directory lookup fails, so a blank name is
 * a hint, not proof), the grant is at least an hour old, and the directory says
 * its principal's object id does not exist.
 */
async function provenOrphans(candidates: RoleAssignmentRow[], now = Date.now()): Promise<RoleAssignmentRow[]> {
  const orphans: RoleAssignmentRow[] = [];
  for (const r of candidates) {
    if (r.principalName) continue;
    const created = Date.parse(r.createdOn ?? "");
    if (Number.isNaN(created) || now - created < ORPHAN_MIN_AGE_MS) continue;
    if (await principalIsGone(r.principalId)) orphans.push(r);
  }
  return orphans;
}

async function deleteAssignmentsById(ids: string[]): Promise<void> {
  for (const id of ids) {
    await runAz([
      "rest", "--method", "delete",
      "--url", `https://management.azure.com${id}?api-version=${ROLE_ASSIGNMENT_API}`,
    ]).catch(() => undefined);
  }
}

/**
 * Find and optionally delete ORPHANED gateway-MI subscription-scoped reader roles
 * (Cost Management Reader + Monitoring Reader). The gateway MI is PERMANENT, so a
 * live platform's grants must never be swept. A candidate is ServicePrincipal-typed,
 * exactly sub scope, and either carries the gateway tag (precise, future grants) or
 * holds one of the two reader roles (generic, pre-tag legacy orphans); it is swept
 * only when provenOrphans says so.
 * Deletes only when execute=true. Returns all matched orphans regardless.
 */
export async function sweepOrphanGatewaySubRoles(opts: {
  subscriptionId: string;
  execute: boolean;
}): Promise<{ id: string; description?: string; roleDefinitionName?: string; createdOn?: string }[]> {
  const subScope = `/subscriptions/${opts.subscriptionId}`;
  const all = await listSubscriptionAssignments(opts.subscriptionId);
  const matched = await provenOrphans(all.filter((r) => {
    if (r.principalType !== "ServicePrincipal") return false;
    if (r.scope !== subScope) return false;
    if (r.description === GATEWAY_SUBSCOPE_GRANT_DESCRIPTION) return true;
    return GATEWAY_SUBSCOPE_ROLES.includes(r.roleDefinitionName ?? "");
  }));
  if (opts.execute) await deleteAssignmentsById(matched.map((r) => r.id));
  return matched.map((r) => ({ id: r.id, description: r.description, roleDefinitionName: r.roleDefinitionName, createdOn: r.createdOn }));
}

/**
 * Find and optionally delete orphaned installer Owner@subscription role assignments:
 * ServicePrincipal-typed Owner grants at exactly sub scope, swept only when
 * provenOrphans says so. The installer's description is never enough on its own,
 * because a LIVE install's installer grant carries the same one.
 * Deletes only when execute=true. Returns all matched orphans regardless.
 */
export async function sweepOrphanOwnerAssignments(opts: {
  subscriptionId: string;
  execute: boolean;
}): Promise<{ id: string; description?: string; createdOn?: string }[]> {
  const subScope = `/subscriptions/${opts.subscriptionId}`;
  const all = await listSubscriptionAssignments(opts.subscriptionId);
  const matched = await provenOrphans(all.filter((r) =>
    r.scope === subScope && r.principalType === "ServicePrincipal" && r.roleDefinitionName === "Owner"));
  if (opts.execute) await deleteAssignmentsById(matched.map((r) => r.id));
  return matched.map((r) => ({ id: r.id, description: r.description, createdOn: r.createdOn }));
}

/**
 * Tear down the installer scaffolding: ACI → MI → the MI's
 * role assignments. Assignments are deleted BY ARM ID via the REST API —
 * the robust method when the principal may already be gone
 * (`--assignee`/`--ids`/`--name` all fail for a deleted principal). The
 * platform RG and the gateway's own assignments persist.
 */
export async function reapInstaller(opts: {
  subscriptionId: string;
  resourceGroup: string;
  aciName: string;
  miName: string;
  roleAssignmentIds: string[];
  principalId: string;
}): Promise<void> {
  const swallow = async (p: Promise<unknown>): Promise<void> => { try { await p; } catch { /* idempotent */ } };

  await swallow(runAz(["container", "delete", "--name", opts.aciName, "--resource-group", opts.resourceGroup, "--subscription", opts.subscriptionId, "--yes", "--only-show-errors"]));
  await swallow(runAz(["identity", "delete", "--name", opts.miName, "--resource-group", opts.resourceGroup, "--subscription", opts.subscriptionId]));
  for (const id of opts.roleAssignmentIds) {
    await swallow(runAz([
      "rest", "--method", "delete",
      "--url", `https://management.azure.com${id}?api-version=${ROLE_ASSIGNMENT_API}`,
    ]));
  }

  // Belt-and-suspenders: a deleted MI's sub-scope Owner can survive if the captured
  // id list was stale/empty. The assignment record keeps principalId after deletion.
  const survivors = JSON.parse(
    (await runAz([
      "role", "assignment", "list", "--all", "--subscription", opts.subscriptionId,
      "--query", `[?principalId=='${opts.principalId}' && roleDefinitionName=='Owner'].id`, "-o", "json",
    ]).catch(() => "[]")) || "[]",
  ) as string[];
  for (const id of survivors) {
    await swallow(runAz(["rest", "--method", "delete", "--url", `https://management.azure.com${id}?api-version=${ROLE_ASSIGNMENT_API}`]));
  }
}
